import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, ShoppingBag, Wine, Truck, Gift, ChevronRight, Sparkles } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { settingsStore } from '@/services/apiService';
import { useState, useEffect, useMemo } from 'react';
import ProductImage from '@/components/ProductImage';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer = ({ isOpen, onClose }: CartDrawerProps) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { items, updateQuantity, removeFromCart, totalPrice } = useCart();
  const [settings, setSettings] = useState(settingsStore.get());

  useEffect(() => {
    const unsubscribe = settingsStore.subscribe(setSettings);
    return unsubscribe;
  }, []);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const freeThreshold = settings?.freeShippingThreshold || 0;
  const progressPercent = useMemo(() => {
    if (freeThreshold <= 0) return 100;
    return Math.min(100, (totalPrice / freeThreshold) * 100);
  }, [totalPrice, freeThreshold]);
  const isFreeShipping = freeThreshold > 0 && totalPrice >= freeThreshold;
  const missingForFree = freeThreshold > 0 ? Math.max(0, freeThreshold - totalPrice) : 0;

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 right-0 top-0 z-[100] flex w-full max-w-[440px] flex-col overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--card)) 100%)',
              boxShadow: '-12px 0 48px rgba(0,0,0,0.12)',
            }}
          >
            {/* === HEADER === */}
            <div className="relative border-b border-border/40">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/3" />
              <div className="relative flex items-center justify-between px-6 py-5">
                <div className="flex items-center gap-3.5">
                  <div className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10">
                    <ShoppingBag className="h-5 w-5 text-primary" />
                    {items.length > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground shadow-sm"
                      >
                        {totalItems}
                      </motion.div>
                    )}
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-bold tracking-tight">
                      {t('cart')}
                    </h2>
                    {items.length > 0 && (
                      <p className="text-[11px] text-muted-foreground/70">
                        {totalItems} {totalItems === 1
                          ? (language === 'PL' ? 'produkt' : 'item')
                          : totalItems < 5
                            ? (language === 'PL' ? 'produkty' : 'items')
                            : (language === 'PL' ? 'produktów' : 'items')
                        }
                      </p>
                    )}
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-muted/50 transition-colors hover:bg-muted"
                >
                  <X className="h-4 w-4" />
                </motion.button>
              </div>
            </div>

            {/* === FREE SHIPPING PROGRESS BAR === */}
            {freeThreshold > 0 && items.length > 0 && (
              <div className="border-b border-border/30 px-6 py-3.5" style={{ background: 'hsl(var(--muted) / 0.3)' }}>
                <div className="flex items-center gap-2 mb-2.5">
                  {isFreeShipping ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex items-center gap-2"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                        <Gift className="h-3.5 w-3.5 text-green-600" />
                      </div>
                      <span className="text-xs font-bold text-green-600">
                        {language === 'PL' ? 'Darmowa dostawa!' : 'Free shipping!'}
                      </span>
                      <Sparkles className="h-3 w-3 text-green-500" />
                    </motion.div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                        <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {language === 'PL'
                          ? `Brakuje ${missingForFree.toFixed(2)} zł do darmowej dostawy`
                          : `${missingForFree.toFixed(2)} zł to free shipping`}
                      </span>
                    </div>
                  )}
                </div>
                <div className="relative h-2 overflow-hidden rounded-full bg-border/40">
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    style={{
                      background: isFreeShipping
                        ? 'linear-gradient(90deg, #16a34a, #4ade80)'
                        : 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.6))',
                    }}
                  />
                </div>
              </div>
            )}

            {/* === CONTENT === */}
            <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              {items.length === 0 ? (
                /* Pusty koszyk */
                <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1, type: 'spring' }}
                    className="mb-6 flex h-28 w-28 items-center justify-center rounded-3xl"
                    style={{ background: 'linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--muted) / 0.3) 100%)' }}
                  >
                    <Wine className="h-12 w-12 text-muted-foreground/30" />
                  </motion.div>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-2 font-display text-xl font-bold text-foreground/70"
                  >
                    {t('emptyCart')}
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-sm text-muted-foreground/60"
                  >
                    {language === 'PL'
                      ? 'Odkryj nasze wina i dodaj je tutaj'
                      : 'Explore our wines and add them here'}
                  </motion.p>
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { onClose(); navigate('/produkty'); }}
                    className="mt-8 flex items-center gap-2 rounded-xl bg-primary/10 px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/15"
                  >
                    {language === 'PL' ? 'Przeglądaj produkty' : 'Browse products'}
                    <ChevronRight className="h-4 w-4" />
                  </motion.button>
                </div>
              ) : (
                /* Lista produktów */
                <div className="px-5 py-5 space-y-3">
                  <AnimatePresence mode="popLayout">
                    {items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 80, filter: 'blur(4px)' }}
                        transition={{ delay: index * 0.04 }}
                        className="group relative overflow-hidden rounded-2xl bg-card border border-border/30 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:border-border/50"
                      >
                        <div className="flex gap-3.5 p-3.5">
                          {/* Zdjęcie produktu */}
                          <div className="relative h-[90px] w-[72px] flex-shrink-0 overflow-hidden rounded-xl bg-[#F9F7F2]">
                            <ProductImage
                              src={item.image}
                              alt={item.name}
                              loading="lazy"
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                          </div>

                          {/* Detale */}
                          <div className="flex flex-1 flex-col justify-between min-w-0 py-0.5">
                            <div className="pr-7">
                              <h4 className="text-[13px] font-semibold text-foreground leading-snug line-clamp-2">
                                {item.name}
                              </h4>
                              {item.inStoreOnly && (
                                <div className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 self-start px-1.5 py-0.5 rounded border border-amber-200/50">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-store"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h2V14h8v8h2a2 2 0 0 0 2-2v-8"/><path d="M2 12h20"/><path d="M22 7v3a2 2 0 0 1-2 2v0a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12v0a2 2 0 0 1-2-2V7"/></svg>
                                  {language === 'PL' ? 'Tylko odbiór osobisty' : 'Pickup only'}
                                </div>
                              )}
                              <div className="mt-0.5 flex items-center gap-2">
                                <p className="text-[11px] text-muted-foreground/60">
                                  {item.price.toFixed(2)} zł / szt.
                                </p>
                                {item.originalPrice && (
                                  <>
                                    <span className="text-[10px] text-muted-foreground/40 line-through">
                                      {item.originalPrice.toFixed(2)} zł
                                    </span>
                                    <span className="bg-red-50 text-red-600 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">
                                      {language === 'PL' ? 'Promocja' : 'Sale'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="flex items-end justify-between mt-1">
                              {/* Kontrolki ilości */}
                              <div className="flex items-center gap-0.5 rounded-lg bg-muted/50 p-0.5">
                                <motion.button
                                  whileHover={{ scale: 1.15 }}
                                  whileTap={{ scale: 0.85 }}
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  className="flex h-7 w-7 items-center justify-center rounded-md text-foreground/60 transition-colors hover:bg-background hover:text-foreground hover:shadow-sm"
                                >
                                  <Minus className="h-3 w-3" />
                                </motion.button>
                                <span className="w-8 text-center text-xs font-bold tabular-nums text-foreground">
                                  {item.quantity}
                                </span>
                                <motion.button
                                  whileHover={{ scale: 1.15 }}
                                  whileTap={{ scale: 0.85 }}
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  disabled={item.maxStock !== undefined && item.quantity >= item.maxStock}
                                  className="flex h-7 w-7 items-center justify-center rounded-md text-foreground/60 transition-colors hover:bg-background hover:text-foreground hover:shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <Plus className="h-3 w-3" />
                                </motion.button>
                              </div>

                              {/* Cena */}
                              <p className="font-display text-sm font-bold text-primary tabular-nums">
                                {(item.price * item.quantity).toFixed(2)} zł
                              </p>
                            </div>
                          </div>

                          {/* Przycisk usuwania */}
                          <motion.button
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.8 }}
                            onClick={() => removeFromCart(item.id)}
                            className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full opacity-100 sm:opacity-0 transition-all sm:group-hover:opacity-100 text-muted-foreground/50 hover:text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* === FOOTER === */}
            {items.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="border-t border-border/40"
                style={{ background: 'hsl(var(--card))' }}
              >
                {/* Podsumowanie */}
                <div className="px-6 pt-5 pb-4 space-y-2.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground/70">
                      {language === 'PL' ? 'Wartość produktów' : 'Products value'}
                    </span>
                    <span className="font-medium tabular-nums">{totalPrice.toFixed(2)} zł</span>
                  </div>

                  {freeThreshold > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground/70">
                        {language === 'PL' ? 'Dostawa' : 'Delivery'}
                      </span>
                      <span className={`text-xs font-semibold ${isFreeShipping ? 'text-green-600' : 'text-muted-foreground/60'}`}>
                        {isFreeShipping
                          ? (language === 'PL' ? 'GRATIS ✓' : 'FREE ✓')
                          : (language === 'PL' ? 'Obliczana przy zamówieniu' : 'Calculated at checkout')}
                      </span>
                    </div>
                  )}

                  <div className="h-px bg-gradient-to-r from-transparent via-border/50 to-transparent" />

                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold">{t('total')}</span>
                    <span className="font-display text-2xl font-bold text-primary tabular-nums">
                      {totalPrice.toFixed(2)} zł
                    </span>
                  </div>
                </div>

                {/* Przycisk zamówienia */}
                <div className="px-6 pb-6">
                  <motion.button
                    whileHover={{ scale: 1.015, y: -1 }}
                    whileTap={{ scale: 0.985 }}
                    onClick={handleCheckout}
                    className="relative w-full overflow-hidden rounded-2xl py-4 text-center font-bold text-white uppercase tracking-widest text-sm shadow-lg"
                    style={{
                      background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.8))',
                      boxShadow: '0 8px 32px hsl(var(--primary) / 0.3)',
                    }}
                  >
                    {/* Efekt shimmer */}
                    <motion.div
                      className="absolute inset-0 -skew-x-12"
                      animate={{
                        x: ['-200%', '200%'],
                      }}
                      transition={{
                        repeat: Infinity,
                        repeatDelay: 5,
                        duration: 1.8,
                        ease: 'easeInOut',
                      }}
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)',
                        width: '100%',
                      }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-2.5">
                      {t('checkout')}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </motion.button>

                  <p className="mt-3.5 text-center text-[10px] text-muted-foreground/50 tracking-wider uppercase">
                    {language === 'PL'
                      ? '🔒 Bezpieczne zakupy · Szyfrowane połączenie'
                      : '🔒 Secure checkout · Encrypted connection'}
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CartDrawer;
